import Button from '@/components/UI/Button/Button';
import {
  type BenchlingLinkedUser,
  type BenchlingLinkStatus,
  disconnectBenchlingAuth,
  fetchBenchlingAuthStatus,
  fetchBenchlingLinkedUser,
  startBenchlingAuth,
} from '@/components/Account/BenchlingAuth/ConnectedAccount.utils';
import { errorToast, successToast } from '@/helpers';
import { _isNotBlank, _notNil } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const statusLabels: Record<BenchlingLinkStatus, string> = {
  NOT_LINKED: 'Not linked',
  LINKED: 'Linked',
  LINK_EXPIRED: 'Link expired - refresh to continue',
};

/**
 * Reports what the OAuth callback came back with, as a toast.
 *
 * Only reasons a user can act on are worded specifically. Everything else falls through to the
 * generic message with the reason appended, which is enough to correlate with a server log without
 * putting our internals in front of them.
 */
const toastCallbackOutcome = (result: string, reason: string | null): void => {
  if (result === 'success') {
    successToast('Benchling account linked');
    return;
  }

  if (reason === 'unbound_user_agent') {
    errorToast('That link attempt did not come from this browser, so it was rejected.');
    return;
  }

  // Our own misconfiguration, not a Benchling rejection — nothing was sent. Worded so the user does
  // not retry, because retrying cannot help until the server is configured.
  if (reason === 'client_not_configured') {
    errorToast('Benchling account linking is not configured on this environment. Contact support.');
    return;
  }

  errorToast(`Could not link your Benchling account${_isNotBlank(reason) ? ` (${reason})` : ''}`);
};

/**
 * Links the acting user's In Vivo account to their Benchling Core account, so delegated actions are
 * attributed to them rather than to the integration's service account.
 *
 * <p>Sits as its own card beneath the profile/password tabs on the Account screen.
 */
const ConnectedAccount: FC = () => {
  const [status, setStatus] = useState<BenchlingLinkStatus | null>(null);
  const [linkedUser, setLinkedUser] = useState<BenchlingLinkedUser | null>(null);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const busy = linking || unlinking;
  const [searchParams, setSearchParams] = useSearchParams();

  const reportStatusFailure = useCallback((error: unknown): void => {
    ExceptionHandler.captureException(
      new InVivoError('Failed to load Benchling link status', {
        cause: error,
        slug: 'benchling-auth-status',
      })
    );
    errorToast('Could not load your Benchling link status');
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchBenchlingAuthStatus()
      .then((next) => {
        if (!cancelled) {
          setStatus(next);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          reportStatusFailure(error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [reportStatusFailure]);

  useEffect(() => {
    const result = searchParams.get('benchling_auth');
    if (!_notNil(result)) {
      return;
    }

    toastCallbackOutcome(result, searchParams.get('reason'));

    // Clear the parameters so a refresh does not re-toast a stale outcome.
    const next = new URLSearchParams(searchParams);
    next.delete('benchling_auth');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const link = async (): Promise<void> => {
    setLinking(true);
    try {
      // A full navigation, not a popup: Benchling drives login, SSO and consent on its own origin and
      // redirects the browser back to our callback.
      window.location.assign(await startBenchlingAuth());
    } catch (error) {
      setLinking(false);
      ExceptionHandler.captureException(
        new InVivoError('Failed to start Benchling delegated auth', {
          cause: error,
          slug: 'benchling-auth-start',
        })
      );
      errorToast('Could not start linking your Benchling account');
    }
  };

  const unlink = async (): Promise<void> => {
    setUnlinking(true);
    try {
      await disconnectBenchlingAuth();
      setLinkedUser(null);
      setStatus('NOT_LINKED');
      successToast('Benchling account unlinked. In Vivo can no longer act on your behalf.');
    } catch (error) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to unlink Benchling account', {
          cause: error,
          slug: 'benchling-auth-disconnect',
        })
      );
      errorToast('Could not unlink your Benchling account');
    } finally {
      setUnlinking(false);
    }
  };

  const hasLink = status === 'LINKED' || status === 'LINK_EXPIRED';

  useEffect(() => {
    if (!hasLink) {
      return;
    }

    let cancelled = false;
    fetchBenchlingLinkedUser()
      .then((user) => {
        if (!cancelled) {
          setLinkedUser(user);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLinkedUser(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasLink]);

  // Gated on hasLink as well as the value: a name is only ever meaningful next to a link, so this
  // cannot render an identity belonging to one the user no longer has.
  const linkedUserLabel =
    hasLink && _notNil(linkedUser)
      ? (linkedUser.handle ?? linkedUser.name ?? linkedUser.email ?? linkedUser.benchling_user_api_id)
      : null;

  return (
    <div className="mv3 ui-card mw7" data-testid="connected-account">
      <div className="pa4">
        <h3 className="f4 mt0 mb2">Connected account</h3>
        <p className="mb3 mid-gray">Link your account to your Benchling core account to enable delegated actions.</p>
        <p className="mb1" data-testid="connected-account__status">
          {_notNil(status) ? statusLabels[status] : 'Loading…'}
        </p>
        {_isNotBlank(linkedUserLabel) && (
          <p className="mb3 f6 mid-gray" data-testid="connected-account__linked-user">
            Linked as {linkedUserLabel} in Benchling
          </p>
        )}
        <div className="flex items-center">
          <Button primary disabled={busy} loading={linking} onClick={link} testId="connected-account__link">
            {hasLink ? 'Refresh' : 'Link account'}
          </Button>
          {hasLink && (
            <Button
              outline
              className="ml2"
              disabled={busy}
              loading={unlinking}
              onClick={unlink}
              testId="connected-account__unlink"
            >
              Unlink account
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectedAccount;
